/**
 * The language manifest published by the connector for an event that ordered
 * machine translation. Absent for captioning-only events.
 *
 * `identifier` is the unique event id. It lets a viewer loaded via a reused
 * static-embed key resolve translated languages to the event's unique-keyed
 * per-language artifacts. All language values are browser_navigator_codes.
 */
export interface Manifest {
  identifier: string;
  machine_translation: {
    source_language: string;
    target_languages: string[];
  };
}

/**
 * The S3 host/prefix the client polls for all live-asr artifacts.
 */
const S3_ORIGIN = "https://originp3.s3.amazonaws.com/live-asr";

/**
 * Fetch the language manifest for a caption key.
 *
 * A non-OK response resolves to `null` rather than throwing: an absent manifest
 * is the normal case for a captioning-only event, or for an MT event whose
 * connector has not yet written the manifest. Callers treat `null` as "no
 * languages" and leave the caption path untouched.
 */
export async function fetchManifest(
  key: string,
  signal?: AbortSignal
): Promise<Manifest | null> {
  const response = await fetch(`${S3_ORIGIN}/${key}_manifest.json`, {
    method: "GET",
    signal,
  });

  if (!response.ok) {
    return null;
  }

  return (await response.json()) as Manifest;
}

/**
 * Resolve which S3 stream a selected language polls.
 *
 * The source language — and every no-manifest or unselected state — stays on the
 * existing unsuffixed `sourceKey`, so captioning-only events and the source
 * stream are byte-for-byte unchanged. A translated language hops to the event's
 * unique-keyed per-language stream via the manifest identifier, which covers
 * both unique-keyed and static-embed loads (the connector does not duplicate
 * per-language streams to the static key). `isTranslation` lets the caller pick
 * the not-yet-available message without repeating this decision.
 */
export function resolveCaptionStream(
  manifest: Manifest | null,
  sourceKey: string | undefined,
  language: string | undefined
): { key: string | undefined; isTranslation: boolean } {
  const isTranslation =
    manifest !== null &&
    language !== undefined &&
    language !== manifest.machine_translation.source_language;

  return {
    key: isTranslation ? `${manifest.identifier}_${language}` : sourceKey,
    isTranslation,
  };
}
